/**
 * The EW job page's Invoices tab (fieldiq-api#1300, EW-6):
 *
 *   • Holdback / retainage — the API's withheld, released and outstanding
 *     figures for the job (GET /earthworks/jobs/:id/holdback), labelled from
 *     the tenant's country. "Release holdback" is offered only while something
 *     is outstanding and no release invoice is live.
 *   • Invoices — this job's invoices, and "Invoice approved tickets", which
 *     puts every approved ticket of the job waiting to be invoiced on one draft
 *     (POST /invoices/from-tickets {job_id}).
 *
 * Writes are admin / office staff; everyone else reads.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useCreateEwInvoiceFromTickets, useEwHoldback, useEwInvoices, useReleaseEwHoldback } from '../../../hooks/useEwInvoices';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { ewDay, ewRefusal, fmtMoney, fmtPct } from '../../../lib/earthworks';
import { canWriteEwInvoices, ewHoldbackCanRelease, ewInvoiceKindText, ewInvoiceStatusSkin } from '../../../lib/ew-invoices';
import { useAuth } from '../../../contexts/AuthContext';
import type { EwInvoiceRow } from '../../../types/ew-invoices';
import { EW_PRIMARY, EW_SECONDARY, EwBadge, EwEmpty, EwFormError, EwLoading, EwPanel, EwRegister, EwSpec, type EwColumn } from '../EwBits';

export function EwHoldbackPanel({ jobId, currency, canWrite, onOpen }: {
  jobId: string; currency: string | null; canWrite: boolean; onOpen: (path: string) => void;
}) {
  const q = useEwHoldback(jobId);
  const release = useReleaseEwHoldback();
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const h = q.data ?? null;
  const label = h?.label || 'Holdback';
  const failure = queryLoadFailure(q);
  const mayRelease = canWrite && ewHoldbackCanRelease(h);

  const go = () => {
    setError(null);
    release.mutate({ job_id: jobId }, {
      onSuccess: (inv) => {
        toast.success(inv?.invoiceNumber ? `${inv.invoiceNumber} drafted` : `${label} release drafted`);
        setConfirm(false);
        if (inv?.id) onOpen(`/earthworks/invoices/${inv.id}`);
      },
      onError: (err) => setError(ewRefusal(err, `The ${label.toLowerCase()} could not be released.`)),
    });
  };

  return (
    <EwPanel
      eyebrow="Billing"
      title={h?.label || 'Holdback / retainage'}
      testId="ew-job-holdback"
      actions={
        mayRelease ? (
          <button type="button" className={EW_PRIMARY} onClick={() => { setError(null); setConfirm(true); }} data-testid="ew-job-release-holdback">
            <Undo2 className="size-4" aria-hidden="true" />
            Release {label.toLowerCase()}
          </button>
        ) : undefined
      }
    >
      {q.isLoading ? (
        <EwLoading label={`Loading the ${label.toLowerCase()}`} />
      ) : !h ? (
        <div className="px-4 py-4">
          <LoadFailureNotice error={queryFailureError(q)} kind={failure ?? 'unreachable'} subject={`this job's ${label.toLowerCase()}`} />
        </div>
      ) : (
        <>
          <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
            <EwSpec label="Rate" value={h.enabled ? fmtPct(h.pct) : 'Off on this job'} />
            <EwSpec label="Withheld" value={<span data-testid="ew-holdback-withheld">{fmtMoney(h.withheld, currency)}</span>} mono />
            <EwSpec label="Released" value={<span data-testid="ew-holdback-released">{fmtMoney(h.released, currency)}</span>} mono />
            <EwSpec label="Outstanding" value={<span data-testid="ew-holdback-outstanding">{fmtMoney(h.outstanding, currency)}</span>} mono />
          </dl>
          {h.releaseInvoice && (
            <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-holdback-release-invoice">
              Released on{' '}
              <Link className="font-mono font-bold underline" to={`/earthworks/invoices/${h.releaseInvoice.id}`}>{h.releaseInvoice.invoiceNumber}</Link>{' '}
              ({ewInvoiceStatusSkin(h.releaseInvoice.status).label}).
            </p>
          )}
          {confirm && mayRelease && (
            <div className="space-y-3 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-holdback-confirm">
              <p>Drafts one invoice billing the {fmtMoney(h.outstanding, currency)} withheld on this job. Invoices raised after it withhold nothing.</p>
              <EwFormError message={error} />
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" className={EW_SECONDARY} onClick={() => setConfirm(false)} disabled={release.isPending}>Cancel</button>
                <button type="button" className={EW_PRIMARY} onClick={go} disabled={release.isPending} data-testid="ew-holdback-confirm-release">
                  {release.isPending ? 'Drafting…' : 'Draft the release invoice'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </EwPanel>
  );
}

export function EwJobInvoicesSection({ job, onOpen }: {
  job: { id: string; status: string; currency?: string | null };
  onOpen: (path: string) => void;
}) {
  const { user } = useAuth();
  const canWrite = canWriteEwInvoices(user?.role);
  const q = useEwInvoices({ job_id: job.id });
  const create = useCreateEwInvoiceFromTickets();
  const [error, setError] = useState<string | null>(null);
  const rows = q.data ?? [];

  const doCreate = () => {
    setError(null);
    create.mutate({ job_id: job.id }, {
      onSuccess: (inv) => {
        toast.success(inv?.invoiceNumber ? `${inv.invoiceNumber} drafted` : 'Invoice drafted');
        if (inv?.id) onOpen(`/earthworks/invoices/${inv.id}`);
      },
      onError: (err) => setError(ewRefusal(err, 'The invoice could not be created.')),
    });
  };

  const columns: EwColumn<EwInvoiceRow>[] = [
    { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
    { key: 'kind', header: 'Kind', render: (r) => ewInvoiceKindText(r.kind) },
    { key: 'status', header: 'Status', render: (r) => <EwBadge skin={ewInvoiceStatusSkin(r.status)} value={r.status} /> },
    { key: 'date', header: 'Invoice date', render: (r) => ewDay(r.invoiceDate) },
    { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono">{fmtMoney(r.total, r.currency)}</span> },
    { key: 'balance', header: 'Balance', align: 'right', render: (r) => <span className="font-mono">{fmtMoney(r.balanceDue, r.currency)}</span> },
  ];

  return (
    <div className="space-y-4">
      <EwHoldbackPanel jobId={job.id} currency={job.currency ?? null} canWrite={canWrite} onOpen={onOpen} />
      <EwPanel
        eyebrow="Billing"
        title="Invoices"
        testId="ew-job-invoices"
        actions={
          canWrite ? (
            <button type="button" className={EW_PRIMARY} onClick={doCreate} disabled={create.isPending} data-testid="ew-job-create-invoice">
              <FilePlus2 className="size-4" aria-hidden="true" />
              {create.isPending ? 'Creating…' : 'Invoice approved tickets'}
            </button>
          ) : undefined
        }
      >
        {error && <div className="px-4 pt-3"><EwFormError message={error} /></div>}
        <EwRegister
          query={q}
          rows={rows}
          columns={columns}
          rail={(r) => ewInvoiceStatusSkin(r.status).rail}
          onOpen={(r) => onOpen(`/earthworks/invoices/${r.id}`)}
          subject="this job's invoices"
          testId="ew-job-invoice-row"
          minWidth="min-w-[720px]"
          empty={<EwEmpty>No invoice bills this job yet.</EwEmpty>}
          card={(r) => (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
                <p className="mt-0.5 text-xs text-fieldiq-muted">{ewInvoiceKindText(r.kind)} · {fmtMoney(r.total, r.currency)}</p>
              </div>
              <EwBadge skin={ewInvoiceStatusSkin(r.status)} value={r.status} />
            </div>
          )}
        />
      </EwPanel>
    </div>
  );
}
